import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faHandHoldingDollar, faArrowUpRightFromSquare, faFlag } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

import Feedback from "$components/feedback";
import LargeNumberRange from "$components/table/cells/large-number-range";
import ProbabilityBubble from "$components/probability";
import { getTerm } from "$stores/dictionary";
import { closeModal, initiateModal } from "$stores/modal";

// Detail row in the panel
const DetailRow = ({ label, value }) =>
    value ? (
        <div className="opp-panel__detail-row">
            <span className="opp-panel__detail-label">{label}</span>
            <span className="opp-panel__detail-value">{value}</span>
        </div>
    ) : null;

const OpportunityPanel = ({ opportunity, onClose, onFlagged }) => {
    const [localFlagged, setLocalFlagged] = useState(false);

    useEffect(() => {
        setLocalFlagged(false);
    }, [opportunity?.projectKey]);

    if (!opportunity) return null;

    const isUserFlagged = opportunity.userFlagged || localFlagged;

    const handleFlag = () => {
        initiateModal(
            Feedback,
            {
                id: opportunity.id,
                projectKey: opportunity.projectKey,
                onSubmit: () => {
                    closeModal();
                    setLocalFlagged(true);
                    if (onFlagged) onFlagged(opportunity.projectKey);
                },
                analyticsLabel: getTerm("potentialProject"),
            },
            { analyticsLabel: "Flag" }
        );
    };

    return (
        <>
            <div className="opp-panel__backdrop" onClick={onClose} />
            <div className="opp-panel">
                <div className="opp-panel__header">
                    <div className="opp-panel__header-text">
                        <h3 className="opp-panel__title">{opportunity.name}</h3>
                        <p className="opp-panel__subtitle txt -small -light">
                            {opportunity.clientGroupName}
                            {opportunity.clientName && opportunity.clientName !== opportunity.clientGroupName
                                ? ` · ${opportunity.clientName}`
                                : ""}
                        </p>
                    </div>
                    <button className="button -text opp-panel__close" onClick={onClose} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="opp-panel__stats">
                    <div className="opp-panel__stat">
                        <span className="opp-panel__stat-label">Probability</span>
                        <ProbabilityBubble value={opportunity.probability} />
                    </div>
                    <div className="opp-panel__stat">
                        <span className="opp-panel__stat-label">{getTerm("averageRevenue")}</span>
                        <LargeNumberRange value={opportunity.averageRevenue} decimalPrecision={0} />
                    </div>
                </div>

                <div className="opp-panel__details">
                    <DetailRow label={getTerm("projectTypeGroup")} value={opportunity.projectTypeGroup} />
                    <DetailRow label={getTerm("serviceLine")} value={opportunity.serviceLine} />
                    <DetailRow label={getTerm("industry")} value={opportunity.industry} />
                    <DetailRow label="Partner" value={opportunity.crmName} />
                    <DetailRow label={`${getTerm("subClient")} Size`} value={opportunity.clientSize} />
                </div>

                {(opportunity.isCrossSell || opportunity.requiresIndependence) && (
                    <div className="opp-panel__tags">
                        {opportunity.isCrossSell && (
                            <span className="pill -importance">
                                <FontAwesomeIcon icon={faHandHoldingDollar} /> Cross-Sell
                            </span>
                        )}
                        {opportunity.requiresIndependence && (
                            <span className="pill -independence">Check Conflicts</span>
                        )}
                    </div>
                )}

                {opportunity.decisionStatus && (
                    <div className="opp-panel__decision">
                        <span className="opp-panel__detail-label">Decision</span>
                        <span className={`badge ${opportunity.decisionStatus === "pursue" ? "badge-primary" : "badge-secondary"}`}>
                            {opportunity.decisionStatus}
                        </span>
                        {opportunity.decisionNote && (
                            <p className="opp-panel__decision-note txt -small -light">"{opportunity.decisionNote}"</p>
                        )}
                    </div>
                )}

                <div className="opp-panel__footer">
                    <Link
                        to={`/project/${opportunity.projectKey}/potential`}
                        className="button -text -small"
                        onClick={onClose}
                    >
                        View Full Detail <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </Link>
                    {isUserFlagged ? (
                        <span className="opp-panel__flag-indicator" title="Flagged">
                            <FontAwesomeIcon icon={faFlag} /> Flagged
                        </span>
                    ) : (
                        <button className="button -secondary -small -icon" title="Flag" onClick={handleFlag}>
                            <FontAwesomeIcon icon={faFlag} /> Flag
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default OpportunityPanel;
